import { INGEST_PATH, LIMITS } from '@l3ns/shared/constants';
import type { WireEnvelope } from '@l3ns/shared/types';
import { doc, now, on } from '../auto/util';
import { ect } from './context';
import { dlog } from './safe';
import { st, VERSION } from './state';

let timer: ReturnType<typeof setInterval> | undefined;

/**
 * Would this body exceed the transport quota in BYTES? sendBeacon and keepalive
 * fetch both budget bytes, not UTF-16 chars, so non-ASCII payloads can blow the
 * limit well under 60k chars — and the keepalive fallback then rejects too,
 * losing the envelope silently. Under 20k chars nothing can reach 60 KB (3
 * bytes/char worst case), so the encode only runs when it could matter. Guarded:
 * flush() runs in an unwrapped interval and must never throw into the host.
 */
function over(b: string): boolean {
	if (b.length < 2e4) return false;
	try {
		return new TextEncoder().encode(b).length >= 6e4;
	} catch {
		return b.length >= 6e4;
	}
}

/** Flush the queue as one batched envelope. Never throws, never blocks. */
export function flush(reason = 'timer'): void {
	if (!st.key_ || !st.consent_ || !st.session_ || (!st.q_.length && !st.n_.length)) return;
	const t0 = performance.now();
	st.session_.conn = ect(); // effectiveType can change mid-session (wifi → cellular)
	const events = st.q_.splice(0, LIMITS.MAX_EVENTS_PER_ENVELOPE);
	// net rows ride along; shrink net first, then events, until the body fits the
	// quota — leftovers are put back and drain on the next flush
	let nNet = Math.min(st.n_.length, LIMITS.MAX_NET_PER_ENVELOPE);
	let nEv = events.length;
	let body: string;
	let fits: boolean;
	for (;;) {
		const net = nNet ? st.n_.slice(0, nNet) : undefined;
		body = JSON.stringify({
			v: 1,
			key: st.key_,
			sentAt: now(),
			sdk: { name: 'l3ns', version: VERSION },
			session: st.session_,
			events: events.slice(0, nEv),
			...(net && { net }),
		} satisfies WireEnvelope);
		fits = !over(body);
		if (fits) break;
		if (nNet) nNet = nNet > 10 ? 10 : nNet > 3 ? 3 : 0;
		else if (nEv > 1) nEv = nEv >> 1;
		else break; // a single oversized event: send and let the server decide
	}
	st.n_.splice(0, nNet);
	st.q_.unshift(...events.slice(nEv));
	const url = st.api_ + INGEST_PATH;

	// String body ⇒ text/plain ⇒ CORS simple request, without a preflight.
	let ok = false;
	try {
		// Beacon lets the browser schedule small event batches as background work,
		// including page exit. Network bursts must bypass its shared 64 KB quota.
		ok = reason != 'network' && fits && navigator.sendBeacon(url, body);
	} catch {}
	if (!ok)
		try {
			void fetch(url, {
				method: 'POST',
				body,
				// Active-page fallback must not hit the same quota that can reject
				// beacon. Keepalive is reserved for fitting page-exit batches.
				keepalive: fits && (reason == 'hidden' || reason == 'pagehide'),
				priority: 'low',
			}).catch(() => {}); // don't resend an ambiguous failure: it may have arrived
			ok = true;
		} catch {}
	st.diag_.flushes++;
	st.diag_.sent += nEv;
	st.diag_.lastFlushMs = Math.round((performance.now() - t0) * 100) / 100;
	dlog('flush', reason, nEv, 'events', ok ? 'sent' : 'failed');
	// if more than one envelope's worth remains, the interval drains it
}

export function startTransport(): void {
	if (timer == undefined) {
		timer = setInterval(() => flush('timer'), st.flushMs_);
		on(doc, 'visibilitychange', () => {
			if (doc.hidden) flush('hidden');
		});
		on(window, 'pagehide', () => flush('pagehide'));
	}
}
